/**
 * LISS Embroidery Block — Simple, Reliable Implementation
 * Adds embroidery as a separate product to cart
 */

;(() => {
  // Configuration
  const EMBROIDERY_SURCHARGE = Number(window.LISS_EMBROIDERY_PRICE || 2000)
  const EMBROIDERY_VARIANT_ID = Number(window.LISS_EMBROIDERY_VARIANT_ID) || 0
  const EMBROIDERY_PRODUCT_HANDLE = window.LISS_EMBROIDERY_PRODUCT_HANDLE || 'embroidery-personalization'
  
  // Only apply embroidery to specific products (pillowcases)
  // Maps product ID to pillowcase color for preview
  const EMBROIDERY_PRODUCTS = {
    '7396292427847': { name: 'Burgundy Pillowcase', color: '#851521' },
    '7396726472775': { name: 'Olive Pillowcase', color: '#978C45' },
    '7396731453511': { name: 'Crème Pillowcase', color: '#F9F5E9' },
  }
  
  console.log('LISS: Embroidery block loaded. Enabled products:', Object.keys(EMBROIDERY_PRODUCTS))

  // Constants
  const FONTS = {
    font1: { name: "Classic Italic", class: "liss-embroidery-font--1" },
    font2: { name: "Script", class: "liss-embroidery-font--2" },
    font3: { name: "Elegant", class: "liss-embroidery-font--3" },
    font4: { name: "Flourish", class: "liss-embroidery-font--4" },
  }

  const THREAD_COLORS = {
    black: { name: "Black", hex: "#121212" },
    creme: { name: "Crème", hex: "#F9F5E9" },
    olive: { name: "Olive", hex: "#978C45" },
    burgundy: { name: "Burgundy", hex: "#851521" },
    blue: { name: "Sky Blue", hex: "#7AABDE" },
  }

  const PLACEMENTS = {
    top_right: "Top Right Corner",
    center: "In the Middle",
  }

  // State
  let config = {
    enabled: false,
    color: "burgundy",
    placement: "top_right",
    text: "",
    font: "font1",
    threadColor: "black",
  }

  let savedConfig = null
  const elements = {}

  // Add single item to cart (exact format as product-form.js)
  async function addToCart(variantId, quantity = 1, properties = {}) {
    // Ensure variantId is a valid number
    const variantIdNum = Number(variantId)
    if (!variantIdNum || variantIdNum <= 0) {
      throw new Error(`Invalid variant ID: ${variantId}`)
    }

    const formData = new FormData()
    formData.append('id', String(variantIdNum))
    formData.append('quantity', String(quantity))

    // Add properties only if provided
    if (properties && Object.keys(properties).length > 0) {
      Object.entries(properties).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          formData.append(`properties[${key}]`, String(value))
        }
      })
    }

    // Get cart section IDs for drawer updates
    const cartItemsComponents = document.querySelectorAll('cart-items-component')
    const sectionIds = []
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId)
      }
    })

    // Add sections to FormData for cart drawer updates
    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','))
      formData.append('sections_url', window.location.pathname)
    }

    console.log('LISS: Adding to cart:', {
      variantId: String(variantIdNum),
      quantity: String(quantity),
      properties: Object.keys(properties).length > 0 ? properties : 'none',
      sections: sectionIds.length > 0 ? sectionIds.join(',') : 'none'
    })

    // Log FormData contents (for debugging)
    const formDataEntries = []
    for (const [key, value] of formData.entries()) {
      formDataEntries.push(`${key}=${value}`)
    }
    console.log('LISS: FormData:', formDataEntries.join('&'))

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error('LISS: Cart add failed:', response.status, errorText)
      console.error('LISS: Request details:', {
        variantId: String(variantIdNum),
        variantIdType: typeof variantIdNum,
        quantity: String(quantity),
        formData: formDataEntries.join('&')
      })
      
      // If variant not found, provide detailed troubleshooting
      if (response.status === 422) {
        console.error('LISS: ⚠️ 422 Error - Variant not found')
        console.error('LISS: Please verify in Shopify Admin:')
        console.error('LISS: 1. Go to: https://admin.shopify.com/store/azxf0t-7q/products/7391490310215')
        console.error('LISS: 2. Check variant ID: 42391923228743')
        console.error('LISS: 3. Ensure variant is PUBLISHED (not Draft)')
        console.error('LISS: 4. Ensure "Available for purchase" is CHECKED')
        console.error('LISS: 5. Ensure variant is in ONLINE STORE sales channel')
        console.error('LISS: 6. Check inventory - ensure quantity > 0 if tracking enabled')
        console.error('LISS: 7. Try copying the variant ID directly from the URL when viewing the variant')
      }
      
      let errorJson
      try {
        errorJson = JSON.parse(errorText)
      } catch (e) {
        throw new Error(`Failed to add to cart: ${response.status} - ${errorText}`)
      }
      
      // More detailed error message
      const errorMsg = errorJson.message || errorJson.description || 'Failed to add to cart'
      throw new Error(`${errorMsg} (Variant ID: ${variantIdNum})`)
    }

    const result = await response.json()
    
    if (result.status) {
      throw new Error(result.message || result.description || 'Failed to add to cart')
    }

    console.log('LISS: Successfully added to cart')
    return result
  }

  // Intercept form submission
  function bindFormIntercept() {
    const productFormComponent = document.querySelector('product-form-component')
    if (!productFormComponent) {
      setTimeout(bindFormIntercept, 1000)
      return
    }

    const originalHandleSubmit = productFormComponent.handleSubmit.bind(productFormComponent)
    
    productFormComponent.handleSubmit = async function(event) {
      // Check if user has made a choice (either configured embroidery or skipped)
      if (!savedConfig || !savedConfig.enabled) {
        event.preventDefault()
        alert('Please configure embroidery or choose "No, Thanks" before adding to cart.')
        // Open the embroidery drawer
        const embroideryTrigger = document.querySelector('[data-embroidery-trigger]')
        if (embroideryTrigger) {
          embroideryTrigger.click()
        }
        return
      }

      // If user chose "No, Thanks" (skipped), allow normal form submission
      if (savedConfig.skipped) {
        console.log('LISS: Embroidery skipped - adding product without embroidery')
        // Add with "No Embroidery" property for back office tracking
        event.preventDefault()
        
        const form = this.querySelector('form')
        if (!form) return
        
        const formData = new FormData(form)
        const mainVariantId = Number(formData.get('id'))
        const quantity = Number(formData.get('quantity')) || 1
        
        try {
          const result = await addToCart(mainVariantId, quantity, {
            'Embroidery': 'No, Thanks'
          })
          
          const CartAddEvent = window.CartAddEvent || class extends Event {
            constructor(resource, sourceId, data) {
              super('cart:update')
              this.detail = { resource, sourceId, data }
            }
            static eventName = 'cart:update'
          }

          const variantIdString = String(mainVariantId)
          this.dispatchEvent(
            new CartAddEvent({}, variantIdString, {
              source: 'product-form-component',
              itemCount: quantity,
              productId: this.dataset.productId,
              sections: result.sections,
            })
          )

          const cartDrawer = document.querySelector('cart-drawer-component')
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            setTimeout(() => cartDrawer.open(), 50)
          }
          
          console.log('LISS: ✅ Product added without embroidery')
          return
        } catch (err) {
          console.error('LISS: Failed to add to cart:', err)
          alert('Failed to add to cart: ' + err.message)
          return
        }
      }

      // User configured embroidery
      if (!savedConfig.text) {
        event.preventDefault()
        alert('Please enter your embroidery text.')
        const embroideryTrigger = document.querySelector('[data-embroidery-trigger]')
        if (embroideryTrigger) {
          embroideryTrigger.click()
        }
        return
      }

      event.preventDefault()

      const form = this.querySelector('form')
      if (!form) return

      const formData = new FormData(form)
      const mainVariantId = Number(formData.get('id'))
      const quantity = Number(formData.get('quantity')) || 1

      if (!mainVariantId) {
        console.error('LISS: No main variant ID found')
        return
      }

      // Embroidery is now included (no extra charge)
      // Just add the main product with embroidery properties for back office
      try {
        // Build embroidery properties for back office
        // Properties will display as "Name: Value" in cart
        const embroideryProps = {
          'Embroidery Text': savedConfig.text,
          'Embroidery Position': PLACEMENTS[savedConfig.placement] || savedConfig.placement,
          'Embroidery Font': FONTS[savedConfig.font]?.name || savedConfig.font,
          'Embroidery Thread Color': THREAD_COLORS[savedConfig.threadColor]?.name || savedConfig.threadColor,
        }

        console.log('LISS: Adding product with embroidery configuration...')
        console.log('LISS: Embroidery details:', embroideryProps)
        
        // Add main product with embroidery properties
        const result = await addToCart(mainVariantId, quantity, embroideryProps)

        console.log('LISS: Cart add result:', result)
        console.log('LISS: Sections in result:', result.sections)

        // Dispatch cart update event - use the same format as product-form.js
        const CartAddEvent = window.CartAddEvent || class extends Event {
          constructor(resource, sourceId, data) {
            super('cart:update', { bubbles: true })
            this.detail = { resource, sourceId, data }
          }
          static eventName = 'cart:update'
        }

        // Get variant ID as string (like product-form does)
        const variantIdString = String(mainVariantId)

        this.dispatchEvent(
          new CartAddEvent({}, variantIdString, {
            source: 'product-form-component',
            itemCount: quantity,
            productId: this.dataset.productId,
            sections: result.sections || {},
          })
        )

        // Small delay to ensure event is processed
        await new Promise(resolve => setTimeout(resolve, 100))

        // Open cart drawer
        const cartDrawer = document.querySelector('cart-drawer-component')
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open()
        }

        console.log('LISS: ✅ Product added with embroidery configuration!')
        console.log('LISS: Embroidery details will appear in order properties for back office')
      } catch (err) {
        console.error('LISS: Failed to add items:', err)
        alert('Failed to add embroidery to cart: ' + err.message)
      }
    }
  }

  // DOM elements
  function cacheElements() {
    elements.block = document.querySelector("[data-embroidery-block]")
    elements.trigger = document.querySelector("[data-embroidery-trigger]")
    elements.checkbox = document.querySelector("[data-embroidery-checkbox]")
    elements.summary = document.querySelector("[data-embroidery-summary]")
    elements.overlay = document.querySelector("[data-embroidery-overlay]")
    elements.drawer = document.querySelector("[data-embroidery-drawer]")
    elements.closeBtn = document.querySelector("[data-embroidery-close]")
    elements.cancelBtn = document.querySelector("[data-embroidery-cancel]")
    elements.saveBtn = document.querySelector("[data-embroidery-save]")
    elements.skipBtn = document.querySelector("[data-embroidery-skip]")
    elements.editBtn = document.querySelector("[data-embroidery-edit]")
    elements.removeBtn = document.querySelector("[data-embroidery-remove]")
    elements.textInput = document.querySelector("[data-embroidery-text-input]")
    elements.previewPillowcase = document.querySelector("[data-preview-pillowcase]")
    elements.productSwatches = document.querySelector("[data-product-swatches]")
    elements.fontGrid = document.querySelector("[data-font-grid]")
    elements.threadSwatches = document.querySelector("[data-thread-swatches]")
  }

  // Drawer
  function openDrawer() {
    if (!elements.drawer || !elements.overlay) return
    triggerElement = document.activeElement

    config = savedConfig ? { ...savedConfig } : {
      enabled: false,
      color: "burgundy",
      placement: "top_right",
      text: "",
      font: "font1",
      threadColor: "black",
    }

    updateDrawerUI()

    elements.overlay.removeAttribute("hidden")
    elements.drawer.removeAttribute("hidden")
    elements.overlay.offsetHeight
    elements.drawer.offsetHeight
    elements.overlay.classList.add("is-visible")
    elements.drawer.classList.add("is-visible")

    document.body.style.overflow = "hidden"
    document.addEventListener("keydown", handleEscapeKey)
    setTimeout(() => elements.closeBtn?.focus(), 50)
  }

  function closeDrawer(save = false) {
    if (!elements.drawer || !elements.overlay) return

    if (save && config.text) {
      savedConfig = { ...config, enabled: true }
      updateBlockUI(true)
    }

    elements.overlay.classList.remove("is-visible")
    elements.drawer.classList.remove("is-visible")

    setTimeout(() => {
      elements.overlay.setAttribute("hidden", "")
      elements.drawer.setAttribute("hidden", "")
      document.body.style.overflow = ""
      document.removeEventListener("keydown", handleEscapeKey)
      triggerElement?.focus()
    }, 200)
  }

  function handleEscapeKey(e) {
    if (e.key === "Escape") closeDrawer(false)
  }

  // UI Updates
  function updateDrawerUI() {
    if (!elements.drawer) return

    if (elements.textInput) {
      elements.textInput.value = config.text || ""
      updateTextCounter()
    }

    document.querySelectorAll("[data-location-option]").forEach((radio) => {
      radio.checked = radio.value === config.placement
    })

    document.querySelectorAll("[data-font]").forEach((btn) => {
      const isActive = btn.dataset.font === config.font
      if (isActive) {
        btn.classList.add("is-active")
        btn.setAttribute("aria-pressed", "true")
      } else {
        btn.classList.remove("is-active")
        btn.setAttribute("aria-pressed", "false")
      }
    })

    document.querySelectorAll("[data-thread]").forEach((btn) => {
      const isActive = btn.dataset.thread === config.threadColor
      if (isActive) {
        btn.classList.add("is-active")
        btn.setAttribute("aria-pressed", "true")
      } else {
        btn.classList.remove("is-active")
        btn.setAttribute("aria-pressed", "false")
      }
    })

    updatePreview()
  }

  function updateBlockUI(hasConfig) {
    if (!elements.block) return

    if (hasConfig && savedConfig) {
      // Check if user skipped embroidery
      if (savedConfig.skipped) {
        // Show trigger with "skipped" state
        elements.summary?.setAttribute("hidden", "")
        elements.trigger?.removeAttribute("hidden")
        elements.trigger?.classList.remove('liss-embroidery-trigger--not-configured')
        elements.trigger?.classList.add('liss-embroidery-trigger--skipped')
        
        // Update trigger text
        const titleEl = elements.trigger?.querySelector('.liss-embroidery-trigger-title')
        const subtitleEl = elements.trigger?.querySelector('.liss-embroidery-trigger-subtitle')
        const ctaEl = elements.trigger?.querySelector('.liss-embroidery-trigger-cta-text')
        if (titleEl) titleEl.textContent = 'No Embroidery Selected'
        if (subtitleEl) subtitleEl.textContent = 'Click to add personalization if you change your mind'
        if (ctaEl) ctaEl.textContent = 'Add Embroidery'
        return
      }
      
      // Show summary, hide trigger
      elements.summary?.removeAttribute("hidden")
      elements.trigger?.setAttribute("hidden", "")
      elements.trigger?.classList.remove('liss-embroidery-trigger--not-configured')
      elements.trigger?.classList.remove('liss-embroidery-trigger--skipped')

      const textEl = elements.summary?.querySelector("[data-summary-text]")
      const placementEl = elements.summary?.querySelector("[data-summary-placement]")
      const fontEl = elements.summary?.querySelector("[data-summary-font]")
      const threadEl = elements.summary?.querySelector("[data-summary-thread]")

      if (textEl) textEl.textContent = savedConfig.text
      if (placementEl) placementEl.textContent = PLACEMENTS[savedConfig.placement] || savedConfig.placement
      if (fontEl) fontEl.textContent = FONTS[savedConfig.font]?.name || savedConfig.font
      if (threadEl) threadEl.textContent = THREAD_COLORS[savedConfig.threadColor]?.name || savedConfig.threadColor
    } else {
      // Show trigger with "not configured" state
      elements.summary?.setAttribute("hidden", "")
      elements.trigger?.removeAttribute("hidden")
      elements.trigger?.classList.add('liss-embroidery-trigger--not-configured')
      elements.trigger?.classList.remove('liss-embroidery-trigger--skipped')
      
      // Reset trigger text
      const titleEl = elements.trigger?.querySelector('.liss-embroidery-trigger-title')
      const subtitleEl = elements.trigger?.querySelector('.liss-embroidery-trigger-subtitle')
      const ctaEl = elements.trigger?.querySelector('.liss-embroidery-trigger-cta-text')
      if (titleEl) titleEl.textContent = 'Add Your Monogram'
      if (subtitleEl) subtitleEl.textContent = 'Make it uniquely yours with elegant hand-finished embroidery'
      if (ctaEl) ctaEl.textContent = 'Customize Now'
    }
  }

  function updatePreview() {
    if (!elements.previewPillowcase) return
    elements.previewPillowcase.dataset.position = config.placement

    const previewText = elements.previewPillowcase.querySelector("[data-preview-text]")
    if (previewText) {
      previewText.textContent = config.text || ""
      previewText.className = `liss-embroidery-preview-text ${FONTS[config.font]?.class || ""}`
      // Apply thread color to preview text
      const threadColor = THREAD_COLORS[config.threadColor]?.hex || "#1a1a1a"
      previewText.style.color = threadColor
    }
  }

  function updateTextCounter() {
    const counter = document.querySelector("[data-text-counter]")
    if (counter && elements.textInput) {
      counter.textContent = `${elements.textInput.value.length}`
    }
  }

  function updatePillowcaseColor(color) {
    // Update the SVG gradient stops to match the pillowcase color
    const svg = document.querySelector('.liss-embroidery-preview-svg')
    if (!svg) return

    const gradient = svg.querySelector('#pillowGradient')
    if (gradient) {
      const stops = gradient.querySelectorAll('stop')
      if (stops.length >= 2) {
        // Lighter version for first stop
        stops[0].style.stopColor = color
        // Slightly darker for second stop
        stops[1].style.stopColor = color
      }
    }

    // Also update the stroke color for better contrast
    const mainRect = svg.querySelector('rect')
    if (mainRect) {
      // Use dark stroke for light colors, light stroke for dark colors
      const isLightColor = isColorLight(color)
      mainRect.setAttribute('stroke', isLightColor ? '#1a1a1a' : 'rgba(255,255,255,0.5)')
    }

    console.log('LISS: Pillowcase color set to:', color)
  }

  function isColorLight(hexColor) {
    // Remove # if present
    const hex = hexColor.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return luminance > 0.5
  }

  function showTextError(show) {
    const errorEl = document.querySelector("[data-text-error]")
    if (errorEl) errorEl.hidden = !show
    elements.textInput?.classList.toggle("has-error", show)
  }

  // Handlers
  function handleTriggerClick(e) {
    e.preventDefault()
    e.stopPropagation()
    
    if (e.target === elements.checkbox || e.target.closest('[data-embroidery-checkbox]')) {
      const checkbox = elements.checkbox || e.target.closest('[data-embroidery-checkbox]')
      if (checkbox.checked) {
        openDrawer()
      } else {
        if (savedConfig) {
          if (confirm("Remove embroidery customization?")) removeConfig()
          else checkbox.checked = true
        }
      }
      return
    }

    if (elements.checkbox) {
      elements.checkbox.checked = true
    }
    openDrawer()
  }

  function handleTextInput(e) {
    let value = e.target.value
    if (!validateText(value)) {
      showTextError(true)
    } else {
      showTextError(false)
    }
    config.text = value
    updateTextCounter()
    updatePreview()
  }

  function handleLocationChange(e) {
    config.placement = e.target.value
    updatePreview()
  }

  function handleFontClick(e) {
    const btn = e.target.closest("[data-font]")
    if (!btn) return
    e.preventDefault()
    e.stopPropagation()
    config.font = btn.dataset.font
    console.log('LISS: Font selected:', config.font)
    document.querySelectorAll("[data-font]").forEach((b) => {
      const isActive = b === btn
      if (isActive) {
        b.classList.add("is-active")
        b.setAttribute("aria-pressed", "true")
      } else {
        b.classList.remove("is-active")
        b.setAttribute("aria-pressed", "false")
      }
    })
    updatePreview()
  }

  function handleThreadClick(e) {
    const btn = e.target.closest("[data-thread]")
    if (!btn) return
    e.preventDefault()
    e.stopPropagation()
    config.threadColor = btn.dataset.thread
    console.log('LISS: Thread color selected:', config.threadColor)
    document.querySelectorAll("[data-thread]").forEach((b) => {
      const isActive = b === btn
      if (isActive) {
        b.classList.add("is-active")
        b.setAttribute("aria-pressed", "true")
      } else {
        b.classList.remove("is-active")
        b.setAttribute("aria-pressed", "false")
      }
    })
    updatePreview() // Update preview text color
  }

  function handleCancel() {
    closeDrawer(false)
  }

  function handleSave() {
    if (!validateText(config.text)) {
      showTextError(true)
      return
    }
    closeDrawer(true)
  }

  function handleSkip() {
    // User chose "No, Thanks" - mark as skipped (no embroidery)
    savedConfig = {
      enabled: true,
      skipped: true,
      text: '',
      placement: '',
      font: '',
      threadColor: '',
    }
    updateBlockUI(true) // Show as configured (but skipped)
    closeDrawer(false) // Don't save current config
    console.log('LISS: Embroidery skipped by user')
  }

  function handleEdit() {
    openDrawer()
  }

  function handleRemove() {
    if (confirm("Remove embroidery customization?")) removeConfig()
  }

  function removeConfig() {
    savedConfig = null
    config = {
      enabled: false,
      color: "burgundy",
      placement: "top_right",
      text: "",
      font: "font1",
      threadColor: "black",
    }
    updateBlockUI(false)
  }

  function validateText(text) {
    return text && text.trim().length >= 1
  }

  // Events
  function bindEvents() {
    elements.trigger?.addEventListener("click", handleTriggerClick)
    // Checkbox removed - embroidery is now mandatory
    elements.overlay?.addEventListener("click", () => closeDrawer(false))
    elements.closeBtn?.addEventListener("click", () => closeDrawer(false))
    elements.cancelBtn?.addEventListener("click", handleCancel)
    elements.saveBtn?.addEventListener("click", handleSave)
    elements.skipBtn?.addEventListener("click", handleSkip)
    elements.editBtn?.addEventListener("click", handleEdit)
    elements.removeBtn?.addEventListener("click", handleRemove)
    elements.textInput?.addEventListener("input", handleTextInput)

    document.querySelectorAll("[data-location-option]").forEach((input) => {
      input.addEventListener("change", handleLocationChange)
    })

    elements.productSwatches?.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-product-color]")
      if (btn) {
        config.color = btn.dataset.productColor
        updatePreview()
      }
    })

    // Use event delegation for font and thread selection
    elements.fontGrid?.addEventListener("click", (e) => {
      handleFontClick(e)
    })
    elements.threadSwatches?.addEventListener("click", (e) => {
      handleThreadClick(e)
    })
    
    // Also bind directly to buttons as fallback
    document.querySelectorAll("[data-font]").forEach((btn) => {
      btn.addEventListener("click", handleFontClick)
    })
    document.querySelectorAll("[data-thread]").forEach((btn) => {
      btn.addEventListener("click", handleThreadClick)
    })
  }

  // Init
  function init() {
    cacheElements()
    if (!elements.block) return

    // Check if this product has embroidery enabled
    const productId = String(elements.block.dataset.productId || '').trim()
    const productConfig = EMBROIDERY_PRODUCTS[productId]
    
    console.log('LISS: Current product ID:', productId)
    console.log('LISS: Product config:', productConfig)
    
    if (!productConfig) {
      console.log('LISS: ❌ Embroidery not enabled for this product')
      // Hide the embroidery block for other products
      elements.block.style.display = 'none'
      return
    }
    
    console.log('LISS: ✅ Embroidery enabled for:', productConfig.name)

    // Update pillowcase preview color based on product
    updatePillowcaseColor(productConfig.color)

    bindEvents()
    bindFormIntercept()

    if (elements.previewPillowcase) {
      elements.previewPillowcase.dataset.position = config.placement
    }
    updatePreview()

    if (savedConfig) {
      updateBlockUI(true)
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init)
  } else {
    init()
  }

  // Public API
  window.LissEmbroidery = {
    open: openDrawer,
    close: closeDrawer,
    getConfig: () => (savedConfig ? { ...savedConfig } : null),
    setConfig: (newConfig) => {
      if (newConfig && newConfig.text) {
        savedConfig = { ...newConfig, enabled: true }
        updateBlockUI(true)
      } else {
        removeConfig()
      }
    },
    getSurcharge: () => (savedConfig && savedConfig.enabled ? EMBROIDERY_SURCHARGE : 0),
  }
})()
